import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { supabase, priceHelpers } from '../utils/supabase';
import AIVisualizationModal from '../components/AIVisualizationModal';
import {
  StarIcon,
  TruckIcon,
  ShieldCheckIcon,
  CubeIcon,
  ArrowRightIcon,
  PlayIcon,
  EyeIcon,
  Cog6ToothIcon,
  SparklesIcon,
  ShoppingCartIcon,
  TagIcon,
  ChevronRightIcon,
  FireIcon,
  BoltIcon,
  ChevronLeftIcon,
} from '@heroicons/react/24/outline';

const AIVisualizationSlider = () => {
  const [sliderPosition, setSliderPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isDragging || !containerRef.current) return;
      
      const rect = containerRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
      setSliderPosition(percentage);
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    const handleTouchMove = (e) => {
      if (!isDragging || !containerRef.current) return;
      
      const rect = containerRef.current.getBoundingClientRect();
      const x = e.touches[0].clientX - rect.left;
      const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
      setSliderPosition(percentage);
    };

    const handleTouchEnd = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchmove', handleTouchMove);
      document.addEventListener('touchend', handleTouchEnd);

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchend', handleTouchEnd);
      };
    }
  }, [isDragging]);

  const handleMouseDown = (e) => {
    setIsDragging(true);
    e.preventDefault();
  };

  const handleTouchStart = (e) => {
    setIsDragging(true);
    e.preventDefault();
  };

  return (
    <div className="relative rounded-2xl lg:rounded-3xl overflow-hidden shadow-2xl group cursor-ew-resize">
      <div 
        ref={containerRef}
        className="relative w-full h-64 sm:h-80 lg:h-96 select-none"
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
      >
        {/* Normal Görsel (Altalta) */}
        <div className="absolute inset-0">
          <img 
            src="/images/sonrası.jpg" 
            alt="Normal Oda Görünümü"
            className="w-full h-full object-cover"
            draggable={false}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent"></div>
        </div>

        {/* AI ile Üretilmiş Görsel (Üstte, kırpılacak) */}
        <div 
          className="absolute inset-0 overflow-hidden"
          style={{ clipPath: `inset(0 ${100 - sliderPosition}% 0 0)` }}
        >
          <img 
            src="/images/oncesi.jpg" 
            alt="AI ile Yerleştirilmiş Mobilya"
            className="w-full h-full object-cover"
            draggable={false}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-rolex-900/10 to-transparent"></div>
        </div>

        {/* Kaydırıcı Çizgi */}
        <div 
          className="absolute top-0 bottom-0 w-1 bg-white shadow-lg z-10 transition-all duration-200"
          style={{ left: `${sliderPosition}%`, transform: 'translateX(-50%)' }}
        >
          {/* Kaydırıcı Handle */}
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-8 h-8 bg-white rounded-full shadow-lg border-2 border-rolex-600 flex items-center justify-center cursor-grab active:cursor-grabbing">
            <div className="w-1 h-3 bg-rolex-600 rounded-full mx-0.5"></div>
            <div className="w-1 h-3 bg-rolex-600 rounded-full mx-0.5"></div>
          </div>
        </div>

        {/* Etiketler */}
        <div className="absolute top-2 left-2 sm:top-4 sm:left-4 bg-white/90 backdrop-blur-sm px-2 py-1 sm:px-3 sm:py-2 rounded-md sm:rounded-lg opacity-80 group-hover:opacity-100 transition-opacity">
          <div className="flex items-center space-x-1 sm:space-x-2">
            <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-gray-500 rounded-full"></div>
            <span className="text-xs sm:text-sm font-semibold text-gray-800">Normal</span>
          </div>
        </div>

        <div className="absolute top-2 right-2 sm:top-4 sm:right-4 bg-white/90 backdrop-blur-sm px-2 py-1 sm:px-3 sm:py-2 rounded-md sm:rounded-lg opacity-80 group-hover:opacity-100 transition-opacity">
          <div className="flex items-center space-x-1 sm:space-x-2">
            <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-rolex-600 rounded-full animate-pulse"></div>
            <span className="text-xs sm:text-sm font-semibold text-gray-800">AI</span>
          </div>
        </div>

        {/* Alt bilgi */}
        <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 bg-white/90 backdrop-blur-sm px-2 py-1 sm:px-4 sm:py-2 rounded-md sm:rounded-lg opacity-80 group-hover:opacity-100 transition-opacity">
          <span className="text-xs sm:text-sm font-semibold text-gray-800">
            <span className="hidden sm:inline">Kaydırarak karşılaştırın</span>
            <span className="sm:hidden">Kaydır</span>
          </span>
        </div>

        {/* Uyumluluk oranı */}
        <div className="absolute bottom-2 right-2 sm:bottom-4 sm:right-4 bg-rolex-600/90 backdrop-blur-sm px-2 py-1 sm:px-3 sm:py-2 rounded-md sm:rounded-lg text-white opacity-80 group-hover:opacity-100 transition-opacity">
          <span className="text-xs sm:text-sm font-semibold">
            <span className="hidden sm:inline">%98 Uyumluluk</span>
            <span className="sm:hidden">%98</span>
          </span>
        </div>
      </div>
    </div>
  );
};

const InteractiveHeroSection = ({ heroProducts = [] }) => {
  const [currentProduct, setCurrentProduct] = useState(0);
  const [autoRotate, setAutoRotate] = useState(true);

  useEffect(() => {
    if (!autoRotate) return;
    
    const interval = setInterval(() => {
      setCurrentProduct((prev) => (prev + 1) % heroProducts.length);
    }, 4000);

    return () => clearInterval(interval);
  }, [autoRotate, heroProducts.length]);

  // Veri henüz yüklenmemişse loading göster
  if (heroProducts.length === 0) {
    return (
      <section className="relative py-16 lg:py-24 bg-gradient-to-br from-gray-50 via-white to-emerald-50 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-emerald-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Ürünler yükleniyor...</p>
        </div>
      </section>
    );
  }

  return (
    <section className="relative py-16 lg:py-24 bg-gradient-to-br from-gray-50 via-white to-emerald-50 overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0">
        <motion.div
          animate={{ 
            x: [0, 30, 0],
            y: [0, -20, 0],
            rotate: [0, 10, 0] 
          }}
          transition={{ 
            duration: 20, 
            repeat: Infinity, 
            ease: "easeInOut" 
          }}
          className="absolute top-1/4 left-1/4 w-64 h-64 bg-gradient-to-br from-emerald-200/30 to-green-200/30 rounded-full blur-3xl"
        />
        <motion.div
          animate={{ 
            x: [0, -40, 0],
            y: [0, 30, 0],
            rotate: [0, -15, 0] 
          }}
          transition={{ 
            duration: 25, 
            repeat: Infinity, 
            ease: "easeInOut" 
          }}
          className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-gradient-to-br from-blue-200/20 to-indigo-200/20 rounded-full blur-3xl"
        />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          
          {/* Sol: Ana İçerik */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-8"
          >
            {/* Ana Başlık */}
            <div className="space-y-4">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-emerald-100 to-green-100 rounded-full text-emerald-700 text-sm font-medium"
              >
                <SparklesIcon className="h-4 w-4 mr-2" />
                AI Destekli Mobilya Deneyimi
              </motion.div>
              
              <h1 className="text-4xl lg:text-6xl font-bold text-gray-900 leading-tight">
                Evinizi{' '}
                <span className="bg-gradient-to-r from-emerald-600 via-green-500 to-emerald-600 bg-clip-text text-transparent">
                  Dönüştürün
                </span>
                <br />
                <span className="text-2xl lg:text-4xl font-medium text-gray-600">
                  Hayalinizdeki Mekânı Yaratın
                </span>
              </h1>
              
              <p className="text-lg text-gray-600 max-w-xl">
                Yapay zeka teknolojisi ile ürünlerimizi evinizde önizleyin. 
                Premium kalite, güvenilir teslimat ve uzman kurulum.
              </p>
            </div>

            {/* Özellikler */}
            <div className="grid grid-cols-2 gap-4 py-6">
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0 w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <BoltIcon className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900">AI Yerleştirme</p>
                  <p className="text-sm text-gray-600">Anında önizleme</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <TruckIcon className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900">Ücretsiz Teslimat</p>
                  <p className="text-sm text-gray-600">Avrupa geneli</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0 w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <CubeIcon className="h-5 w-5 text-emerald-600" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900">3D Tasarım</p>
                  <p className="text-sm text-gray-600">Profesyonel plan</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0 w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                  <ShieldCheckIcon className="h-5 w-5 text-yellow-600" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900">2 Yıl Garanti</p>
                  <p className="text-sm text-gray-600">Tam güvence</p>
                </div>
              </div>
            </div>

            {/* CTA Butonları */}
            <div className="flex flex-col sm:flex-row gap-4">
              <motion.div
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Link 
                  to="/products" 
                  className="inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r from-emerald-600 to-green-500 text-white font-semibold rounded-xl hover:from-emerald-700 hover:to-green-600 transition-all duration-200 shadow-lg hover:shadow-xl"
                >
                  <ShoppingCartIcon className="h-5 w-5 mr-2" />
                  Ürünleri Keşfet
                  <ChevronRightIcon className="h-5 w-5 ml-2" />
                </Link>
              </motion.div>
              
              <motion.div
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Link 
                  to="/design" 
                  className="inline-flex items-center justify-center px-8 py-4 bg-white text-gray-900 font-semibold rounded-xl border-2 border-gray-200 hover:border-emerald-300 hover:bg-emerald-50 transition-all duration-200"
                >
                  <CubeIcon className="h-5 w-5 mr-2" />
                  3D Evinizi Tasarlayın
                </Link>
              </motion.div>
            </div>
          </motion.div>

          {/* Sağ: Interactive Product Showcase */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative"
            onMouseEnter={() => setAutoRotate(false)}
            onMouseLeave={() => setAutoRotate(true)}
          >
            <div className="relative bg-white rounded-3xl shadow-2xl overflow-hidden">
              {/* Ürün Görseli */}
              <div className="relative h-64 lg:h-80">
                <img 
                  src={heroProducts[currentProduct].image}
                  alt={heroProducts[currentProduct].name}
                  className="w-full h-full object-cover transition-all duration-500"
                />
                
                {/* Ürün Badge */}
                <div className="absolute top-4 left-4">
                  <span className={`px-3 py-1 rounded-full text-xs font-bold text-white ${
                    heroProducts[currentProduct].badge === 'YENİ' ? 'bg-green-500' :
                    heroProducts[currentProduct].badge === 'İNDİRİM' ? 'bg-red-500' :
                    'bg-emerald-500'
                  }`}>
                    {heroProducts[currentProduct].badge}
                  </span>
                </div>

                {/* AI Powered Badge */}
                <div className="absolute top-4 right-4 bg-black/70 backdrop-blur-sm px-3 py-2 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <SparklesIcon className="h-4 w-4 text-emerald-400" />
                    <span className="text-xs font-semibold text-white">AI Powered</span>
                  </div>
                </div>
              </div>

              {/* Ürün Bilgileri */}
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  {heroProducts[currentProduct].name}
                </h3>
                <p className="text-gray-600 mb-4">
                  {heroProducts[currentProduct].description}
                </p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <span className="text-2xl font-bold text-emerald-600">
                      {heroProducts[currentProduct].price}
                    </span>
                    <span className="text-lg text-gray-400 line-through">
                      {heroProducts[currentProduct].originalPrice}
                    </span>
                  </div>
                  <button className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors">
                    İncele
                  </button>
                </div>
              </div>
            </div>

            {/* Ürün Seçici Noktalar */}
            <div className="flex justify-center space-x-2 mt-6">
              {heroProducts.map((_, index) => (
                <button
                  key={index}
                  onClick={() => {
                    setCurrentProduct(index);
                    setAutoRotate(false);
                  }}
                  className={`w-3 h-3 rounded-full transition-all duration-200 ${
                    index === currentProduct 
                      ? 'bg-emerald-600 w-8' 
                      : 'bg-gray-300 hover:bg-gray-400'
                  }`}
                />
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const ProductSlider = ({ products, navigate, addToCart, openAIModal }) => {
  const scrollRef = useRef(null);
  
  const scroll = (direction) => {
    const container = scrollRef.current;
    if (container) {
      const scrollAmount = 320; // width of card + gap
      const scrollLeft = direction === 'left' 
        ? container.scrollLeft - scrollAmount 
        : container.scrollLeft + scrollAmount;
      
      container.scrollTo({
        left: scrollLeft,
        behavior: 'smooth'
      });
    }
  };

  if (!products || products.length === 0) {
    return (
      <div className="lg:col-span-2 flex items-center justify-center h-48 bg-gray-50 rounded-2xl">
        <p className="text-gray-500">Bu kategoride henüz ürün bulunmamaktadır.</p>
      </div>
    );
  }

  return (
    <div className="lg:col-span-2 relative group">
      {/* Navigation Buttons */}
      <button
        onClick={() => scroll('left')}
        className="absolute left-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 bg-white/90 hover:bg-white shadow-lg rounded-full flex items-center justify-center transition-all duration-300 opacity-0 group-hover:opacity-100"
      >
        <ChevronLeftIcon className="h-6 w-6 text-gray-700" />
      </button>
      
      <button
        onClick={() => scroll('right')}
        className="absolute right-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 bg-white/90 hover:bg-white shadow-lg rounded-full flex items-center justify-center transition-all duration-300 opacity-0 group-hover:opacity-100"
      >
        <ChevronRightIcon className="h-6 w-6 text-gray-700" />
      </button>

      {/* Scroll Container */}
      <div 
        ref={scrollRef}
        className="overflow-x-auto scrollbar-hide scroll-smooth"
      >
        <div className="flex space-x-6 pb-4" style={{ width: 'max-content' }}>
          {products.map((product, index) => {
            // Görsel URL'sini belirle
            let imageUrl = product.image_url;
            if (Array.isArray(product.images) && product.images.length > 0) {
              const mainImage = product.images.find(img => img.is_main);
              imageUrl = mainImage?.url || product.images[0]?.url || product.image_url;
            }

            // Fiyat ve badge belirleme
            const displayPrice = product.is_set 
              ? product.set_final_price 
              : (product.sale_price || product.price);
            const originalPrice = product.is_set 
              ? product.set_original_price 
              : (product.sale_price ? product.price : null);

            let badge = "YENİ";
            let badgeColor = "bg-green-500";
            if (originalPrice && originalPrice > displayPrice) {
              const discountPercent = Math.round(((originalPrice - displayPrice) / originalPrice) * 100);
              badge = `%${discountPercent}`;
              badgeColor = "bg-red-500";
            } else if (product.is_featured) {
              badge = "ÖNE ÇIKAN";
              badgeColor = "bg-blue-500";
            }

            return (
              <motion.div
                key={product.id}
                whileHover={{ y: -8, scale: 1.02 }}
                className="flex-none w-72 bg-white rounded-2xl shadow-lg hover:shadow-xl overflow-hidden group/card cursor-pointer transition-all duration-300"
                onClick={() => navigate(`/products/${product.id}`)}
              >
                <div className="relative h-48">
                  <img 
                    src={imageUrl || '/images/koltuk.jpg'}
                    alt={product.name}
                    className="w-full h-full object-cover group-hover/card:scale-110 transition-transform duration-700"
                  />
                  <div className={`absolute top-3 right-3 ${badgeColor} text-white px-2 py-1 rounded-lg text-sm font-semibold`}>
                    {badge}
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent group-hover/card:from-black/20 transition-all duration-300" />
                  
                  {/* Action buttons */}
                  <div className="absolute bottom-3 right-3 flex space-x-2 opacity-0 group-hover/card:opacity-100 transition-all duration-300">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        addToCart(product);
                      }}
                      className="w-9 h-9 bg-white/90 hover:bg-emerald-600 hover:text-white text-gray-700 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110"
                      title="Sepete Ekle"
                    >
                      <ShoppingCartIcon className="h-4 w-4" />
                    </button>
                    {/* Evinde Gör butonu - sadece AI görseli olan ürünlerde göster */}
                    {((product.ai_images && Array.isArray(product.ai_images) && product.ai_images.length > 0) ||
                      (product.ai_image_url && product.ai_image_url.trim() !== '')) && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openAIModal(product);
                        }}
                        className="w-9 h-9 bg-white/90 hover:bg-emerald-600 hover:text-white text-gray-700 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110"
                        title="Evinde Gör"
                      >
                        <SparklesIcon className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
                <div className="p-4">
                  <h4 className="font-semibold text-gray-900 mb-2 group-hover/card:text-rolex-600 transition-colors truncate">
                    {product.name}
                  </h4>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-lg font-bold text-rolex-600">₺{displayPrice?.toLocaleString()}</div>
                      {originalPrice && (
                        <div className="text-sm text-gray-500 line-through">₺{originalPrice?.toLocaleString()}</div>
                      )}
                    </div>
                    <div className="flex items-center space-x-1">
                      <StarIcon className="h-4 w-4 text-yellow-400 fill-current" />
                      <span className="text-sm text-gray-600">{product.rating?.toFixed(1) || '4.5'}</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const HomePage = () => {
  const navigate = useNavigate();
  const { addToCart: addToCartContext } = useCart();
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [featuredPackages, setFeaturedPackages] = useState([]);
  const [heroProducts, setHeroProducts] = useState([]);
  const [categoryProducts, setCategoryProducts] = useState({
    koltuk: [],
    yatak: [],
    yemek: []
  });
  const [loading, setLoading] = useState(true);
  
  // AI Visualization Modal State
  const [aiModalOpen, setAiModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Öne çıkan ürünleri getir
        const { data: products } = await supabase
          .from('products')
          .select('*')
          .eq('is_featured', true)
          .limit(6);

        // Kategorileri getir
        const { data: categoriesData } = await supabase
          .from('categories')
          .select('id, name, description, image_url, is_active')
          .eq('is_active', true)
          .order('name');

        // Öne çıkan paketleri getir
        const { data: packages } = await supabase
          .from('packages')
          .select('*')
          .eq('is_featured', true)
          .eq('is_active', true)
          .limit(3);

        if (products) {
          // Fiyatları format et (kar oranı dahil)
          const formattedProducts = await priceHelpers.formatProductList(
            products.map(product => ({
              ...product,
              rating: 4.5 + Math.random() * 0.5, // Geçici rating
              reviews: Math.floor(Math.random() * 200) + 50, // Geçici review sayısı
              salePrice: product.sale_price // API field mapping
            }))
          );
          
          // Takım ürünleri için doğru fiyat hesaplama
          const productsWithCorrectPrices = await Promise.all(
            formattedProducts.map(async (product) => {
              if (product.is_set && product.set_items && Array.isArray(product.set_items)) {
                // Takım ürünü için kar oranı dahil toplam fiyat hesapla
                let setItemsTotal = 0;
                for (const item of product.set_items) {
                  const salePrice = await priceHelpers.calculateSalePrice(item.price);
                  setItemsTotal += item.quantity * salePrice;
                }
                
                // İndirim hesapla
                const settings = await priceHelpers.getSettings();
                const discount = Math.round(setItemsTotal * (settings.set_discount_rate / 100) * 100) / 100;
                const finalPrice = setItemsTotal - discount;
                
                return {
                  ...product,
                  set_original_price: setItemsTotal,
                  set_discount: discount,
                  set_final_price: finalPrice
                };
              }
              return product;
            })
          );
          
          console.log('HomePage - Products with correct prices:', productsWithCorrectPrices); // Debug için
          setFeaturedProducts(productsWithCorrectPrices);
          
          // Hero section için rastgele 3 ürün seç
          if (productsWithCorrectPrices.length >= 3) {
            const shuffled = [...productsWithCorrectPrices].sort(() => 0.5 - Math.random());
            const selectedHeroProducts = shuffled.slice(0, 3).map(product => {
              // Görsel URL'sini belirle
              let imageUrl = product.image_url;
              if (Array.isArray(product.images) && product.images.length > 0) {
                const mainImage = product.images.find(img => img.is_main);
                imageUrl = mainImage?.url || product.images[0]?.url || product.image_url;
              }
              
              // Badge belirleme
              let badge = "POPÜLER";
              if (product.sale_price && product.price > product.sale_price) {
                badge = "İNDİRİM";
              } else if (product.is_featured) {
                badge = "YENİ";
              }
              
              // Fiyat formatı
              const displayPrice = product.is_set 
                ? product.set_final_price 
                : (product.sale_price || product.price);
              const originalPrice = product.is_set 
                ? product.set_original_price 
                : (product.sale_price ? product.price : null);
              
              return {
                id: product.id,
                name: product.name,
                price: `₺${displayPrice?.toLocaleString()}`,
                originalPrice: originalPrice ? `₺${originalPrice?.toLocaleString()}` : null,
                image: imageUrl,
                badge: badge,
                description: product.description || "Premium kalite ve modern tasarım"
              };
            });
            
            setHeroProducts(selectedHeroProducts);
          }
        }

        if (categoriesData) {
          // Her kategori için ürün sayısını hesapla
          const categoriesWithCount = await Promise.all(
            categoriesData.map(async (cat) => {
              const { count: productCount } = await supabase
                .from('products')
                .select('*', { count: 'exact', head: true })
                .eq('category', cat.name)
                .eq('is_active', true);
              
              return {
                ...cat,
                image: cat.image_url, // image_url alanını image olarak kullan (UI uyumluluğu için)
                count: `${productCount || 0}+ Ürün`,
                color: getRandomGradient()
              };
            })
          );
          
          setCategories(categoriesWithCount);
        }

        if (packages) {
          // Her paket için içeriklerini getir
          const packagesWithItems = await Promise.all(
            packages.map(async (pkg) => {
              // Admin panelindeki method'u direk kullan
              const { data: items } = await supabase
                .from('package_items')
                .select('*')
                .eq('package_id', pkg.id);

              const itemsWithDetails = await Promise.all(
                items?.map(async (item) => {
                  if (item.item_type === 'product') {
                    const { data: product } = await supabase
                      .from('products')
                      .select('*')
                      .eq('id', item.item_id)
                      .single();
                    
                    return { ...item, products: product, product_sets: null };
                  } else if (item.item_type === 'product_set') {
                    const { data: productSet } = await supabase
                      .from('product_sets')
                      .select('*')
                      .eq('id', item.item_id)
                      .single();
                    
                    return { ...item, products: null, product_sets: productSet };
                  }
                  return item;
                }) || []
              );

              return { ...pkg, items: itemsWithDetails };
            })
          );

          setFeaturedPackages(packagesWithItems);
        }

        // Kategori bazında ürünleri çek (Koltuk, Yatak, Yemek)
        const categoryMappings = {
          koltuk: ['Koltuk', 'Oturma Odası', 'Koltuk Takımı', 'Living Room'], 
          yatak: ['Yatak', 'Yatak Odası', 'Yatak Takımı', 'Bedroom'],
          yemek: ['Yemek', 'Yemek Odası', 'Yemek Takımı', 'Dining Room']
        };

        const categoryProductsData = {};
        
        for (const [key, categoryNames] of Object.entries(categoryMappings)) {
          try {
            const { data: categoryProductsRaw } = await supabase
              .from('products')
              .select('*')
              .in('category', categoryNames)
              .eq('is_active', true)
              .limit(8);

            if (categoryProductsRaw && categoryProductsRaw.length > 0) {
              // Fiyatları format et
              const formattedCategoryProducts = await Promise.all(
                categoryProductsRaw.map(async (product) => {
                  const formattedProduct = await priceHelpers.formatProductPrice({
                    ...product,
                    rating: 4.5 + Math.random() * 0.5, // Geçici rating
                    reviews: Math.floor(Math.random() * 200) + 50 // Geçici review sayısı
                  });

                  // Takım ürünleri için fiyat hesaplama
                  if (product.is_set && product.set_items && Array.isArray(product.set_items)) {
                    let setItemsTotal = 0;
                    for (const item of product.set_items) {
                      const salePrice = await priceHelpers.calculateSalePrice(item.price);
                      setItemsTotal += item.quantity * salePrice;
                    }
                    
                    const settings = await priceHelpers.getSettings();
                    const discount = Math.round(setItemsTotal * (settings.set_discount_rate / 100) * 100) / 100;
                    const finalPrice = setItemsTotal - discount;
                    
                    return {
                      ...formattedProduct,
                      set_original_price: setItemsTotal,
                      set_discount: discount,
                      set_final_price: finalPrice
                    };
                  }
                  return formattedProduct;
                })
              );
              
              categoryProductsData[key] = formattedCategoryProducts;
            } else {
              categoryProductsData[key] = [];
            }
          } catch (error) {
            console.error(`${key} kategorisi ürünleri yüklenirken hata:`, error);
            categoryProductsData[key] = [];
          }
        }

        setCategoryProducts(categoryProductsData);
      } catch (error) {
        console.error('Data fetch error:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const addToCart = (product) => {
    if (product.is_set) {
      // Takım ürünü için
      const cartItem = {
        id: product.id,
        name: product.name,
        price: product.set_final_price || product.price,
        image: Array.isArray(product.images) 
          ? product.images.find(img => img.is_main)?.url || product.images[0]?.url
          : product.image_url,
        isSet: true,
        category: product.category
      };
      addToCartContext(cartItem, 1);
    } else {
      // Normal ürün için - price artık kar oranı uygulanmış satış fiyatı
      const cartItem = {
        id: product.id,
        name: product.name,
        price: product.price, // formatProductList tarafından kar oranı uygulanmış
        image: Array.isArray(product.images) 
          ? product.images.find(img => img.is_main)?.url || product.images[0]?.url
          : product.image_url,
        isSet: false,
        category: product.category
      };
      addToCartContext(cartItem, 1);
    }
  };

  // AI Visualization Modal Functions
  const openAIModal = (product) => {
    setSelectedProduct(product);
    setAiModalOpen(true);
  };

  const closeAIModal = () => {
    setAiModalOpen(false);
    setSelectedProduct(null);
  };

  const getRandomGradient = () => {
    const gradients = [
      'from-blue-500/40 to-indigo-600/60',
      'from-emerald-400/40 to-teal-600/60',
      'from-amber-400/40 to-orange-500/60',
      'from-slate-400/40 to-gray-600/60',
      'from-emerald-400/40 to-green-600/60',
      'from-rose-400/40 to-pink-600/60',
    ];
    return gradients[Math.floor(Math.random() * gradients.length)];
  };

  const calculateItemsTotal = (items) => {
    return items.reduce((total, item) => {
      // Admin panelindeki gibi fiyat hesapla
      const price = item.price_per_unit || getItemPrice(item);
      return total + (price * item.quantity);
    }, 0);
  };

  const getItemPrice = (item) => {
    if (item.item_type === 'product' && item.products) {
      return item.products.price;
    } else if (item.item_type === 'product_set' && item.product_sets) {
      return item.product_sets.set_price;
    }
    return 0;
  };

  const calculateDiscount = (originalTotal, packagePrice) => {
    if (originalTotal <= 0) return 0;
    return Math.round(((originalTotal - packagePrice) / originalTotal) * 100);
  };

  // Admin panelindeki helper fonksiyonlar
  const getItemDisplayName = (item) => {
    if (item.item_type === 'product' && item.products) {
      return item.products.name;
    } else if (item.item_type === 'product_set' && item.product_sets) {
      return item.product_sets.name;
    }
    return 'Bilinmeyen öğe';
  };

  const getItemImages = (item) => {
    if (item.item_type === 'product' && item.products) {
      return item.products.images || item.products.image_url;
    } else if (item.item_type === 'product_set' && item.product_sets) {
      return item.product_sets.images || item.product_sets.image_url;
    }
    return null;
  };

  // Static data as fallback
  const staticFeaturedProducts = [
    {
      id: 1,
      name: 'Milano Executive Desk',
      price: 24999,
      sale_price: 19999,
      images: ['https://images.unsplash.com/photo-1567538096630-e0c55bd6374c?w=600&h=400&fit=crop&crop=center'],
      category: 'Office',
      rating: 4.9,
      reviews: 127,
    },
    {
      id: 2,
      name: 'Venetian Luxury Sofa',
      price: 45999,
      sale_price: 39999,
      images: ['https://images.unsplash.com/photo-1567538096630-e0c55bd6374c?w=600&h=400&fit=crop&crop=center'],
      category: 'Living Room',
      rating: 4.8,
      reviews: 89,
    },
    {
      id: 3,
      name: 'Royal Dining Set',
      price: 32999,
      images: ['https://images.unsplash.com/photo-1449247709967-d4461a6a6103?w=600&h=400&fit=crop&crop=center'],
      category: 'Dining Room',
      rating: 5.0,
      reviews: 156,
    },
  ];


  const benefits = [
    {
      icon: TruckIcon,
      title: 'Ücretsiz Teslimat',
      description: 'Avrupa geneli',
    },
    {
      icon: ShieldCheckIcon,
      title: '2 Yıl Garanti',
      description: 'Tam güvence',
    },
    {
      icon: CubeIcon,
      title: '3D Tasarım',
      description: 'Sanal dekorasyon',
    },
    {
      icon: SparklesIcon,
      title: 'AI Yerleştirme',
      description: 'Akıllı önizleme',
    },
  ];

  // Sadece veritabanından gelen verileri kullan
  const displayProducts = featuredProducts;
  const displayCategories = categories;

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <InteractiveHeroSection heroProducts={heroProducts} />

      {/* Simple Benefits Bar */}
      <section className="py-8 bg-white border-y border-gray-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center items-center gap-8 lg:gap-12">
            {benefits.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                className="flex items-center space-x-3 group cursor-pointer"
              >
                {/* Simple Icon */}
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                  index === 0 ? 'bg-blue-500 text-white' :
                  index === 1 ? 'bg-emerald-500 text-white' :
                  index === 2 ? 'bg-emerald-500 text-white' :
                  'bg-pink-500 text-white'
                }`}>
                  <benefit.icon className="h-4 w-4" />
                </div>
                
                                 {/* Simple Text */}
                 <div className="text-left">
                   <div className="font-semibold text-gray-900 text-sm group-hover:text-emerald-600 transition-colors">
                     {benefit.title}
                   </div>
                   <div className="text-xs text-gray-500 hidden sm:block">
                     {benefit.description}
                   </div>
                 </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>


      {/* Featured Products */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-luxury font-bold text-gray-900 mb-4">
              Öne Çıkan Ürünler
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              En çok tercih edilen premium mobilyalarımızı keşfedin
            </p>
          </motion.div>

          {loading ? (
            <div className="flex justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rolex-600"></div>
            </div>
          ) : displayProducts.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {displayProducts.map((product, index) => (
                <motion.div
                  key={product.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="relative rounded-3xl overflow-hidden shadow-2xl group cursor-pointer"
                  onClick={() => navigate(`/products/${product.id}`)}
                >
                  <div className="relative h-80">
                    <img 
                      src={
                        Array.isArray(product.images) 
                          ? product.images.find(img => img.is_main)?.url || product.images[0]?.url
                          : product.image_url || 'https://images.unsplash.com/photo-1567538096630-e0c55bd6374c?w=600&h=400&fit=crop&crop=center'
                      } 
                      alt={product.name}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
                    
                    {/* Rating & Reviews - Top Left */}
                    <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm px-3 py-2 rounded-lg">
                      <div className="flex items-center space-x-1">
                        <StarIcon className="h-4 w-4 text-luxury-500 fill-current" />
                        <span className="text-sm font-semibold text-gray-800">{product.rating?.toFixed(1) || '4.5'}</span>
                        <span className="text-sm text-gray-600">({product.reviews || '50'})</span>
                      </div>
                    </div>

                    {/* Sale Badge - Top Right */}
                    {product.is_set && product.set_original_price > (product.set_final_price || product.price) ? (
                      <div className="absolute top-4 right-4 bg-blue-500/90 backdrop-blur-sm px-3 py-2 rounded-lg">
                        <span className="text-sm font-semibold text-white">
                          🛋️ %{Math.round((1 - (product.set_final_price || product.price) / product.set_original_price) * 100)} İndirim
                        </span>
                      </div>
                    ) : (
                      <div className="absolute top-4 right-4 bg-rolex-600/90 backdrop-blur-sm px-3 py-2 rounded-lg">
                        <span className="text-sm font-semibold text-white">
                          {product.category || 'Premium'}
                        </span>
                      </div>
                    )}
                    
                    {/* Product Info - Bottom */}
                    <div className="absolute bottom-4 left-4 right-4 bg-white/90 backdrop-blur-sm px-4 py-3 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="text-sm font-semibold text-gray-800 mb-1">
                            {product.name}
                          </h3>
                                                     <div className="flex items-center space-x-2">
                             {product.is_set ? (
                               <>
                                 <span className="text-lg font-bold text-blue-600">
                                   ₺{(product.set_final_price || product.price || 0).toLocaleString()}
                                 </span>
                                 {product.set_original_price && product.set_original_price > (product.set_final_price || product.price) && (
                                   <span className="text-sm text-gray-500 line-through">
                                     ₺{product.set_original_price.toLocaleString()}
                                   </span>
                                 )}
                                 <span className="text-xs bg-blue-100 text-blue-600 px-1.5 py-0.5 rounded">
                                   🛋️
                                 </span>
                               </>
                             ) : (
                               <>
                                 <span className="text-lg font-bold text-rolex-600">
                                   ₺{(product.price || 0).toLocaleString()}
                                 </span>
                               </>
                             )}
                           </div>
                        </div>
                        
                        <div className="flex space-x-2">
                          {/* Evinde Gör butonu - sadece AI görseli olan ürünlerde göster */}
                          {((product.ai_images && Array.isArray(product.ai_images) && product.ai_images.length > 0) ||
                            (product.ai_image_url && product.ai_image_url.trim() !== '')) && (
                            <motion.button
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                              onClick={(e) => {
                                e.stopPropagation();
                                openAIModal(product);
                              }}
                              className="w-10 h-10 bg-gradient-to-r from-emerald-100 to-green-100 hover:from-emerald-600 hover:to-green-600 hover:text-white text-emerald-600 rounded-lg transition-all duration-300 flex items-center justify-center"
                              title="Evinde Gör"
                            >
                              <SparklesIcon className="h-4 w-4" />
                            </motion.button>
                          )}
                          <motion.button
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={(e) => {
                              e.stopPropagation();
                              addToCart(product);
                            }}
                            className="w-10 h-10 bg-emerald-100 hover:bg-emerald-600 hover:text-white text-emerald-600 rounded-lg transition-all duration-300 flex items-center justify-center"
                            title="Sepete Ekle"
                          >
                            <ShoppingCartIcon className="h-4 w-4" />
                          </motion.button>
                          <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/products/${product.id}`);
                            }}
                            className="bg-rolex-600 hover:bg-rolex-700 text-white px-3 py-2 rounded-lg text-sm font-semibold transition-all duration-300"
                          >
                            İncele
                          </motion.button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Hover effect overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-rolex-900/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                                 </motion.div>
               ))}
             </div>
           ) : (
             <div className="text-center py-12">
               <p className="text-gray-500">Henüz öne çıkan ürün bulunmuyor.</p>
             </div>
           )}
        </div>
      </section>

            {/* Categories and Products Section */}
      <section className="py-24 bg-gradient-to-br from-gray-50 via-white to-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-20"
          >
            <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-rolex-100 to-luxury-100 rounded-full text-rolex-700 text-sm font-medium mb-6">
              <TagIcon className="h-4 w-4 mr-2" />
              Premium Koleksiyonlar
            </div>
            <h2 className="text-5xl font-luxury font-bold text-gray-900 mb-6">
              <span className="block">Yaşam Alanlarınız İçin</span>
              <span className="bg-gradient-to-r from-rolex-600 to-luxury-600 bg-clip-text text-transparent">
                Özel Tasarımlar
              </span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Her kategoride en kaliteli malzemeler ve ustalıkla hazırlanmış, 
              evinizin karakterini yansıtan mobilyalar
            </p>
          </motion.div>

          {/* Categories Grid */}
          <div className="space-y-24">
          
                        {/* New Premium Categories Section */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="space-y-16"
            >
              {/* Koltuk Takımları */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-stretch">
                <motion.div
                  whileHover={{ scale: 1.02, rotateY: 2 }}
                  className="relative h-96 rounded-3xl overflow-hidden shadow-2xl group cursor-pointer"
                  onClick={() => navigate('/products?category=Koltuk Takımları')}
                >
                  <img 
                    src="/images/koltuk.jpg" 
                    alt="Koltuk Takımları"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-600/80 via-blue-500/60 to-transparent" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                  
                  <div className="absolute inset-0 flex flex-col justify-end p-8 text-white">
                    <h3 className="text-3xl font-luxury font-bold mb-3">Koltuk Takımları</h3>
                    <p className="text-white/90 mb-2 text-lg">Premium koltuk takımları</p>
                    <p className="text-white/80 font-semibold mb-6">{categoryProducts.koltuk.length}+ Ürün Çeşidi</p>
                    <div className="flex items-center space-x-3 text-white/90">
                      <span className="font-medium">Koleksiyonu Görüntüle</span>
                      <ArrowRightIcon className="h-5 w-5 transition-transform group-hover:translate-x-2" />
                    </div>
                  </div>
                </motion.div>

                <ProductSlider 
                  products={categoryProducts.koltuk}
                  navigate={navigate}
                  addToCart={addToCart}
                  openAIModal={openAIModal}
                />
              </div>

              {/* Yatak Odası Takımları */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-stretch">
                <ProductSlider 
                  products={categoryProducts.yatak}
                  navigate={navigate}
                  addToCart={addToCart}
                  openAIModal={openAIModal}
                />

                <motion.div
                  whileHover={{ scale: 1.02, rotateY: -2 }}
                  className="relative h-96 rounded-3xl overflow-hidden shadow-2xl group cursor-pointer order-1 lg:order-2"
                  onClick={() => navigate('/products?category=Yatak Takımları')}
                >
                  <img 
                    src="/images/karyola.jpg" 
                    alt="Yatak Odası Takımları"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-br from-emerald-600/80 via-emerald-500/60 to-transparent" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                  
                  <div className="absolute inset-0 flex flex-col justify-end p-8 text-white">
                    <h3 className="text-3xl font-luxury font-bold mb-3">Yatak Odası</h3>
                    <p className="text-white/90 mb-2 text-lg">Lüks yatak takımları</p>
                    <p className="text-white/80 font-semibold mb-6">{categoryProducts.yatak.length}+ Ürün Çeşidi</p>
                    <div className="flex items-center space-x-3 text-white/90">
                      <span className="font-medium">Koleksiyonu Görüntüle</span>
                      <ArrowRightIcon className="h-5 w-5 transition-transform group-hover:translate-x-2" />
                    </div>
                  </div>
                </motion.div>
              </div>

              {/* Yemek Odası Takımları */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-stretch">
                <motion.div
                  whileHover={{ scale: 1.02, rotateY: 2 }}
                  className="relative h-96 rounded-3xl overflow-hidden shadow-2xl group cursor-pointer"
                  onClick={() => navigate('/products?category=Yemek')}
                >
                  <img 
                    src="/images/tvunite.jpeg" 
                    alt="Yemek Odası Takımları"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-br from-orange-600/80 via-orange-500/60 to-transparent" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                  
                  <div className="absolute inset-0 flex flex-col justify-end p-8 text-white">
                    <h3 className="text-3xl font-luxury font-bold mb-3">Yemek Odası</h3>
                    <p className="text-white/90 mb-2 text-lg">Zarif yemek takımları</p>
                    <p className="text-white/80 font-semibold mb-6">{categoryProducts.yemek.length}+ Ürün Çeşidi</p>
                    <div className="flex items-center space-x-3 text-white/90">
                      <span className="font-medium">Koleksiyonu Görüntüle</span>
                      <ArrowRightIcon className="h-5 w-5 transition-transform group-hover:translate-x-2" />
                    </div>
                  </div>
                </motion.div>

                <ProductSlider 
                  products={categoryProducts.yemek}
                  navigate={navigate}
                  addToCart={addToCart}
                  openAIModal={openAIModal}
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

     {/* AI Visualization Experience */}
     <section className="py-12 sm:py-16 lg:py-20 bg-gradient-to-br from-emerald-50 via-white to-green-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-emerald-100 to-green-100 rounded-full text-emerald-700 text-sm font-medium mb-6">
              <SparklesIcon className="h-4 w-4 mr-2" />
              Yapay Zeka Teknolojisi
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-5xl font-bold text-gray-900 mb-4 sm:mb-6">
              Mobilyalarımızı{' '}
              <span className="bg-gradient-to-r from-emerald-600 via-green-500 to-emerald-600 bg-clip-text text-transparent">
                Evinizde Görün
              </span>
            </h2>
            <p className="text-base sm:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto mb-6 sm:mb-8">
              AI destekli teknolojimiz ile ürünlerimizi satın almadan önce evinizde nasıl görüneceğini deneyimleyin. 
              Kaydırarak farkı görün!
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="max-w-4xl mx-auto px-4 sm:px-0"
          >
            <AIVisualizationSlider />
          </motion.div>

          {/* AI Features Grid */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 lg:gap-8 mt-8 sm:mt-12 lg:mt-16"
          >
            <div className="text-center p-4 sm:p-6 bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300">
              <div className="w-16 h-16 bg-gradient-to-r from-green-100 to-emerald-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <EyeIcon className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Anında Önizleme</h3>
              <p className="text-gray-600">
                Ürünü evinizde nasıl görüneceğini gerçek zamanlı olarak görün
              </p>
            </div>

            <div className="text-center p-4 sm:p-6 bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300">
              <div className="w-16 h-16 bg-gradient-to-r from-blue-100 to-indigo-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Cog6ToothIcon className="h-8 w-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Akıllı Uyumluluk</h3>
              <p className="text-gray-600">
                AI, evinizin tarzına en uygun renk ve stil önerilerini sunar
              </p>
            </div>

            <div className="text-center p-4 sm:p-6 bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300">
              <div className="w-16 h-16 bg-gradient-to-r from-emerald-100 to-green-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <PlayIcon className="h-8 w-8 text-emerald-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Kolay Kullanım</h3>
              <p className="text-gray-600">
                Sadece fotoğraf yükleyin, AI gerisini halleder. Tek tıkla deneyim
              </p>
            </div>
          </motion.div>

          {/* CTA Button */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="text-center mt-12"
          >
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <button 
                onClick={() => {
                  // Demo için varsayılan ürün ile modal aç
                  const demoProduct = featuredProducts[0] || {
                    id: 'demo',
                    name: 'Demo Ürün',
                    image_url: '/images/koltuk.jpg'
                  };
                  openAIModal(demoProduct);
                }}
                className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-emerald-600 to-green-500 text-white font-semibold rounded-xl hover:from-emerald-700 hover:to-green-600 transition-all duration-200 shadow-lg hover:shadow-xl"
              >
                <SparklesIcon className="h-5 w-5 mr-2" />
                AI Yerleştirmeyi Deneyin
                <ArrowRightIcon className="h-5 w-5 ml-2" />
              </button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Featured Packages */}
      {featuredPackages.length > 0 && (
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-luxury font-bold text-gray-900 mb-4">
                Öne Çıkan Paketler
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                Özel olarak hazırlanmış paketlerle evinizi tamamlayın
              </p>
            </motion.div>

            <div className="space-y-12">
              {featuredPackages.map((pkg, packageIndex) => {
                const itemsTotal = calculateItemsTotal(pkg.items);
                const packagePrice = parseFloat(pkg.package_price || 0);
                const discount = calculateDiscount(itemsTotal, packagePrice);
                
                return (
                  <motion.div
                    key={pkg.id}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: packageIndex * 0.2 }}
                    className="bg-gradient-to-br from-gray-50 to-white rounded-3xl p-8 shadow-xl border border-gray-100"
                  >
                    {/* Package Header */}
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8">
                      <div className="mb-4 lg:mb-0">
                        <div className="flex items-center space-x-3 mb-2">
                          <TagIcon className="h-6 w-6 text-rolex-600" />
                          <h3 className="text-2xl font-luxury font-bold text-gray-900">
                            {pkg.name}
                          </h3>
                                                     {discount > 0 && (
                             <div className="bg-red-100 text-red-600 px-3 py-1 rounded-full text-sm font-semibold">
                               %{discount} İndirim
                             </div>
                           )}
                        </div>
                        <p className="text-gray-600 text-lg mb-4">
                          {pkg.description}
                        </p>
                        <div className="flex items-center space-x-4">
                          <div className="text-sm text-gray-500">
                                                         <span className="line-through">₺{(itemsTotal || 0).toLocaleString()}</span>
                            <span className="ml-2 text-gray-600">→</span>
                          </div>
                                                     <div className="text-3xl font-bold text-rolex-600">
                             ₺{packagePrice.toLocaleString()}
                           </div>
                        </div>
                      </div>
                      
                      <div className="flex space-x-4">
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          className="btn-outline-luxury"
                        >
                          Paketi İncele
                        </motion.button>
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          className="btn-luxury"
                        >
                          Sepete Ekle
                        </motion.button>
                      </div>
                    </div>

                    {/* Package Items - Responsive Grid */}
                    <div className="relative">
                      <div className={`grid gap-4 ${
                        pkg.items?.length === 1 ? 'grid-cols-1' :
                        pkg.items?.length === 2 ? 'grid-cols-2' :
                        pkg.items?.length === 3 ? 'grid-cols-3' :
                        pkg.items?.length === 4 ? 'grid-cols-2 lg:grid-cols-4' :
                        'grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'
                      }`}>
                        {pkg.items?.map((item, itemIndex) => (
                          <motion.div
                            key={`${item.item_type}-${item.item_id}-${itemIndex}`}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.4, delay: itemIndex * 0.1 }}
                            className="bg-white rounded-2xl shadow-lg overflow-hidden group cursor-pointer hover:shadow-xl transition-all duration-300"
                          >
                            <div className="relative h-32 sm:h-40">
                              <img
                                src={
                                  (() => {
                                    const itemImages = getItemImages(item);
                                    if (Array.isArray(itemImages)) {
                                      return itemImages.find(img => img.is_main)?.url || itemImages[0]?.url;
                                    }
                                    return itemImages || 'https://images.unsplash.com/photo-1567538096630-e0c55bd6374c?w=300&h=200&fit=crop&crop=center';
                                  })()
                                }
                                alt={getItemDisplayName(item)}
                                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                              />
                              <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                              
                              {/* Item Type Badge */}
                              <div className="absolute top-2 left-2 bg-white/90 backdrop-blur-sm px-1.5 py-0.5 rounded-md">
                                <span className="text-xs font-medium text-gray-700">
                                  {item.item_type === 'product' ? 'Ürün' : 'Takım'}
                                </span>
                              </div>

                              {/* Quantity Badge */}
                              {item.quantity > 1 && (
                                <div className="absolute top-2 right-2 bg-rolex-600/90 backdrop-blur-sm px-1.5 py-0.5 rounded-md">
                                  <span className="text-xs font-medium text-white">
                                    {item.quantity}x
                                  </span>
                                </div>
                              )}
                            </div>
                            
                            <div className="p-3">
                              <h4 className="font-medium text-sm text-gray-900 mb-1 truncate">
                                {getItemDisplayName(item)}
                              </h4>
                              <div className="flex items-center justify-between">
                                <div className="text-xs">
                                  <div className="text-gray-900 font-bold">
                                    ₺{((item.price_per_unit || getItemPrice(item)) * item.quantity).toLocaleString()}
                                  </div>
                                  {item.quantity > 1 && (
                                    <div className="text-gray-500 text-xs">
                                      {item.quantity} × ₺{(item.price_per_unit || getItemPrice(item)).toLocaleString()}
                                    </div>
                                  )}
                                </div>
                                <motion.button
                                  whileHover={{ scale: 1.1 }}
                                  whileTap={{ scale: 0.9 }}
                                  className="w-6 h-6 bg-gray-100 hover:bg-rolex-600 hover:text-white text-gray-600 rounded-md transition-all duration-300 flex items-center justify-center"
                                  title="Ürünü İncele"
                                >
                                  <EyeIcon className="h-3 w-3" />
                                </motion.button>
                              </div>
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Categories */}
      <section className="py-24 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-5xl font-luxury font-bold text-gray-900 mb-6">
              Kategorilerimiz
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Her yaşam alanınız için özenle seçilmiş, benzersiz tasarım mobilyalar. 
              Lüks ve konforu bir araya getiren koleksiyonlarımızı keşfedin.
            </p>
          </motion.div>

          {displayCategories.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {displayCategories.map((category, index) => (
              <Link to={`/products?category=${encodeURIComponent(category.name)}`} key={category.name}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.7, delay: index * 0.1 }}
                  whileHover={{ y: -8 }}
                  className="relative h-96 rounded-3xl overflow-hidden group cursor-pointer shadow-xl"
                >
                <div className="absolute inset-0">
                  <img 
                    src={category.image} 
                    alt={category.name}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                </div>
                
                <div className={`absolute inset-0 bg-gradient-to-t ${category.color} transition-opacity duration-500`} />
                
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
                
                <div className="absolute inset-0 flex flex-col justify-end p-8 text-white">
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 + index * 0.1 }}
                  >
                    <h3 className="text-3xl font-luxury font-bold mb-3">
                      {category.name}
                    </h3>
                    <p className="text-white/90 mb-2 text-lg">
                      {category.description}
                    </p>
                    <p className="text-white/80 font-semibold mb-6">
                      {category.count}
                    </p>
                    <motion.div
                      whileHover={{ x: 8 }}
                      className="inline-flex items-center space-x-3 bg-white/10 backdrop-blur-sm px-6 py-3 rounded-xl border border-white/20 hover:bg-white/20 transition-all duration-300"
                    >
                      <span className="font-semibold">Koleksiyonu Görüntüle</span>
                      <ArrowRightIcon className="h-5 w-5" />
                    </motion.div>
                  </motion.div>
                </div>

                {/* Hover effect overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-rolex-900/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                </motion.div>
              </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <div className="max-w-md mx-auto">
                <TagIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Henüz kategori bulunmuyor
                </h3>
                <p className="text-gray-500">
                  Kategoriler yakında eklenecek. Lütfen daha sonra tekrar kontrol edin.
                </p>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* AI Visualization Section */}
      <section className="py-12 sm:py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="inline-flex items-center space-x-2 bg-rolex-50 text-rolex-600 px-4 py-2 rounded-full text-sm font-semibold mb-6">
                <SparklesIcon className="h-4 w-4" />
                <span>Yapay Zeka Teknolojisi</span>
              </div>
              
              <h2 className="text-2xl sm:text-3xl lg:text-5xl font-luxury font-bold text-gray-900 mb-4 sm:mb-6">
                Mobilyalarınızı 
                <span className="block bg-gradient-to-r from-rolex-600 to-luxury-600 bg-clip-text text-transparent">
                  Evinizde Görün
                </span>
              </h2>
              
              <p className="text-base sm:text-lg lg:text-xl text-gray-600 mb-6 sm:mb-8 leading-relaxed">
                Gelişmiş AI teknolojimiz ile seçtiğiniz mobilyaları kendi evinizde, 
                gerçek boyutlarda ve perspektifte görüntüleyin. Satın almadan önce 
                nasıl görüneceğini bilin.
              </p>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-rolex-600 rounded-full"></div>
                  <span className="text-gray-700">Gerçek boyut ve ölçek</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-rolex-600 rounded-full"></div>
                  <span className="text-gray-700">Işık ve gölge simülasyonu</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-rolex-600 rounded-full"></div>
                  <span className="text-gray-700">360° görüntüleme</span>
                </div>
              </div>
              
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="btn-luxury inline-flex items-center space-x-2"
              >
                <EyeIcon className="h-5 w-5" />
                <span>AI Görüntülemeyi Dene</span>
              </motion.button>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <AIVisualizationSlider />
              
              {/* Floating Elements */}
              <motion.div
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 3, repeat: Infinity }}
                className="absolute -top-6 -right-6 w-24 h-24 bg-gradient-to-br from-luxury-400/20 to-luxury-600/20 rounded-full blur-xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* 3D Customization Section */}
      <section className="py-24 luxury-gradient">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="lg:order-2"
            >
              <div className="inline-flex items-center space-x-2 bg-luxury-100 text-luxury-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
                <Cog6ToothIcon className="h-4 w-4" />
                <span>3D Özelleştirme</span>
              </div>
              
              <h2 className="text-4xl lg:text-5xl font-luxury font-bold text-gray-900 mb-6">
                Evinizi 3D Olarak
                <span className="block bg-gradient-to-r from-luxury-600 to-rolex-600 bg-clip-text text-transparent">
                  Tasarlayın
                </span>
              </h2>
              
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                3D tasarım aracımız ile evinizin planını çıkarın, mobilyaları yerleştirin 
                ve en uygun kombinasyonları oluşturun. Profesyonel iç mimarların 
                kullandığı teknoloji artık elinizin altında.
              </p>
              
              <div className="grid grid-cols-2 gap-6 mb-8">
                <div className="text-center">
                  <div className="text-3xl font-bold text-rolex-600 mb-2">15K+</div>
                  <div className="text-gray-600">3D Model</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-luxury-600 mb-2">500+</div>
                  <div className="text-gray-600">Renk Seçeneği</div>
                </div>
              </div>
              
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="btn-gold inline-flex items-center space-x-2"
              >
                <CubeIcon className="h-5 w-5" />
                <span>3D Tasarımı Başlat</span>
              </motion.button>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="lg:order-1 relative"
            >
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img 
                  src="https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?w=800&h=600&fit=crop&crop=center" 
                  alt="3D Tasarım Aracı"
                  className="w-full h-auto"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-luxury-900/20 to-transparent"></div>
                
                {/* 3D Interface Elements */}
                <div className="absolute top-4 left-4 space-y-2">
                  <div className="bg-white/90 backdrop-blur-sm px-3 py-2 rounded-lg">
                    <span className="text-sm font-semibold text-gray-800">3D Görünüm</span>
                  </div>
                  <div className="bg-white/90 backdrop-blur-sm px-3 py-2 rounded-lg">
                    <span className="text-sm text-gray-600">Ölçek: 1:100</span>
                  </div>
                </div>
                
                <div className="absolute bottom-4 left-4 right-4 bg-white/90 backdrop-blur-sm px-4 py-3 rounded-lg">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-gray-800">Milano Desk</span>
                    <span className="text-sm text-luxury-600 font-semibold">₺19.999</span>
                  </div>
                </div>
              </div>
              
              {/* Floating 3D Elements */}
              <motion.div
                animate={{ rotate: [0, 360] }}
                transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                className="absolute -bottom-6 -left-6 w-20 h-20 bg-gradient-to-br from-rolex-400/30 to-rolex-600/30 rounded-full blur-lg"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 luxury-gradient">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="space-y-8"
          >
            <h2 className="text-4xl md:text-5xl font-luxury font-bold text-gray-900 leading-tight">
              Hayalinizdeki Ev
              <br />
              <span className="bg-gradient-to-r from-rolex-600 to-luxury-600 bg-clip-text text-transparent">
                Bir Tık Uzakta
              </span>
            </h2>
            
            <p className="text-xl text-gray-700 max-w-2xl mx-auto">
              Premium kalite, benzersiz tasarım ve mükemmel işçilik. 
              Evinizi lüks bir yaşam alanına dönüştürün.
            </p>
            
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Link 
                to="/products" 
                className="btn-gold inline-flex items-center space-x-2"
                onClick={() => window.scrollTo(0, 0)}
              >
                <span>Alışverişe Başla</span>
                <ArrowRightIcon className="h-5 w-5" />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* AI Visualization Modal */}
      <AIVisualizationModal
        isOpen={aiModalOpen}
        onClose={closeAIModal}
        product={selectedProduct}
      />
    </div>
  );
};

export default HomePage; 